import { FC, useEffect } from 'react';
import moment from 'moment';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/providers/Toast.provider';
import { useClientData } from '@/providers/ClientData.provider';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';
import useElinx from '@/components/Elinx/ElinxHistory/Details/useElinx';
import { Box, formattedCurrency, capitalizeFirstLetterOfWords, withStyles, Card, sanitizeToNumeric } from 'vopay';
import { Title, DetailsRows, TransactionStatus, UtilButton } from 'UI';
import styles from '../Transactions.module.scss';

const cn = withStyles(styles);

type DataProps = {
  transactionId: string;
  hasErrorInfo: boolean;
  failureReason: string;
};

type URLParams = {
  TransactionType: string;
  TransactionID: string;
  Currency: string;
};

const OtherTransactionInfo: FC = () => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.OtherTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');
  const { state } = useLocation<DataProps>();
  const { TransactionID } = useParams<URLParams>();
  const currentDate = moment().utc().format('YYYY-MM-DD');
  const { transactions, fetchTransactionsHistory } = useFetchTransactionsHistory();
  const { details, fetchElinxDetails } = useElinx();
  const toast = useToast();
  const transaction = transactions[0];
  const elinxRecepientName = details?.ReceiverName;
  const {
    response: { clientAccountsEnabled },
  } = useClientData();

  useEffect(() => {
    if (!transaction?.ELinxRequestID) return;

    const getElinxDetails = async () => {
      try {
        await fetchElinxDetails({ ELinxRequestID: transaction?.ELinxRequestID || '' });
      } catch (err) {
        console.error(err);
        toast.neutral(t('ErrorMessages.fetchingError', { defaultValue: 'Failed to fetch eLinx history' }));
      }
    };

    getElinxDetails();
  }, [transaction]);

  useEffect(() => {
    fetchTransactionsHistory({
      StartDateTime: moment().subtract(5, 'years').format('YYYY-MM-DD'),
      EndDateTime: currentDate,
      TransactionID,
    });
  }, [TransactionID, currentDate]);

  if (!transaction) return null;

  const transactionDetails = [
    {
      label: t('TransactionDetails.status', { defaultValue: 'Status' }),
      value: (
        <Box jc="fe">
          <TransactionStatus
            status={transaction.TransactionStatus}
            label={
              capitalizeFirstLetterOfWords(
                tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus || ''}`, {
                  defaultValue: transaction.TransactionStatus,
                }),
              ) || ''
            }
          />
        </Box>
      ),
    },
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: transaction.TransactionType,
    },
    {
      label: t('TransactionDetails.recipientName', { defaultValue: 'Recipient Name' }),
      value: transaction.ClientAccountID ? (
        <UtilButton
          onClick={() => {
            if (clientAccountsEnabled) {
              window.open(`/client-accounts/${transaction.ClientAccountID}`, '_blank');
            } else {
              window.open(`/contacts/${transaction.ClientAccountID}`, '_blank');
            }
          }}
        >
          {elinxRecepientName}
        </UtilButton>
      ) : (
        elinxRecepientName
      ),
      visible: !!elinxRecepientName,
    },
    {
      label: t('TransactionDetails.elinxId', { defaultValue: 'Elinx ID' }),
      value: transaction.ELinxRequestID,
      visible: !!transaction.ELinxRequestID,
    },
    {
      label: t('TransactionDetails.transactionTypeCode', { defaultValue: 'Transaction Type Code' }),
      value: transaction.TransactionTypeCode || '-',
    },
    {
      label: t('TransactionDetails.transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDateTime,
    },
    {
      label: t('TransactionDetails.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified,
    },
    {
      label: t('TransactionDetails.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${parseFloat(transaction.CreditAmount) === 0 ? formattedCurrency(transaction.DebitAmount) : formattedCurrency(transaction.CreditAmount) || '-'}`,
    },
    {
      label: t('TransactionDetails.holdAmount', { defaultValue: 'Hold Amount' }),
      value: transaction.HoldAmount ? `${transaction.Currency} ${formattedCurrency(transaction.HoldAmount)}` : '-',
    },
    {
      label: t('TransactionDetails.clientReferenceNumber', { defaultValue: 'Client Reference Number' }),
      value: transaction.ClientReferenceNumber || '-',
    },
    {
      label: t('TransactionDetails.glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
    },
    {
      label: t('TransactionDetails.paymentRailID', { defaultValue: 'Payment Rail ID' }),
      value: transaction.PaymentRailID || '-',
    },
    {
      label: t('TransactionDetails.notes', { defaultValue: 'Notes' }),
      value: transaction.Notes || '-',
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified,
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${transaction.CreditAmount && Number(sanitizeToNumeric(transaction.CreditAmount)) !== 0 ? formattedCurrency(transaction.CreditAmount) : formattedCurrency(transaction.DebitAmount)}`,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: transaction.TransactionFailureReason || '-',
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsTitle', { defaultValue: 'Transaction Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      {state?.hasErrorInfo && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoTitle', { defaultValue: 'Error Info' })}
          </Title>
          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default OtherTransactionInfo;
